import { CommonModule, isPlatformBrowser } from "@angular/common";
import { Component, inject, OnDestroy, OnInit, PLATFORM_ID } from "@angular/core";
import { FormControl, ReactiveFormsModule } from "@angular/forms";
import { MatDialog } from "@angular/material/dialog";
import { CartAppliedCoupon, CartItem, ServiceFee } from "@models/cart";
import { ApplyCouponResponse } from "@models/coupon";
import { InstallmentOption } from "@models/payment";
import { UserInfo } from "@models/user";
import { AuthService } from "@services/auth.service";
import { CouponsService } from "@services/coupons.service";
import { PendingCouponService } from "@services/pending-coupon.service";
import { SnackBarService } from "@services/snack-bar.service";
import {
  catchError,
  EMPTY,
  finalize,
  interval,
  Subject,
  switchMap,
  takeUntil,
  tap,
} from "rxjs";
import { AppLayoutComponent } from "../../../shared/components/app-layout/app-layout.component";
import { ButtonComponent } from "../../../shared/components/button/button.component";
import { CountdownTimerComponent } from "../../../shared/components/countdown-timer/countdown-timer.component";
import { ModalSignupComponent } from "../../../shared/components/modal-signup/modal-signup.component";
import { UtilsService } from "../../../utils/utils.service";
import { CartService } from "../../services/cart.service";
import { EventsManagementService } from "../../services/events-management.service";
import { EventInfo } from "../../services/types/events-management-request.type";
import { EventCardComponent } from "../../shared/components/cards/event-card/event-card.component";
import { CartAddressSectionComponent } from "./cart-address-section/cart-address-section.component";
import {
  CartClientInfo,
  CartCustomerInfoSectionComponent,
} from "./cart-customer-info-section/cart-customer-info-section.component";
import { CartItemsComponent } from "./cart-items/cart-items.component";
import { CheckoutComponent } from "./checkout/checkout.component";
import { MarketingJourneyContext } from "../../services/marketing/marketing.models";

@Component({
  selector: "app-cart",
  imports: [
    CommonModule,
    ReactiveFormsModule,
    CheckoutComponent,
    CartItemsComponent,
    CountdownTimerComponent,
    AppLayoutComponent,
    ButtonComponent,
    EventCardComponent,
    CartAddressSectionComponent,
    CartCustomerInfoSectionComponent,
  ],
  templateUrl: "./cart.component.html",
  styleUrl: "./cart.component.scss",
})
export class CartComponent implements OnInit, OnDestroy {
  private readonly platformId = inject(PLATFORM_ID);
  constructor(
    private cartService: CartService,
    private utilsService: UtilsService,
    private readonly dialog: MatDialog,
    private readonly eventsManagementService: EventsManagementService,
    private authService: AuthService,
    private readonly couponsService: CouponsService,
    private readonly pendingCoupon: PendingCouponService,
    private readonly snackbarService: SnackBarService,
  ) {}

  couponControl = new FormControl("", { nonNullable: true });
  appliedCoupon: CartAppliedCoupon | null = null;
  couponLoading = false;
  couponError = "";

  cartProducts: CartItem[] = [];
  onlineSalesClosed = false;
  currentUser?: UserInfo;
  // Carrinho é single-event: um único evento para todos os itens.
  eventInfo?: EventInfo;
  limitDatetime!: Date;
  sellerId?: string;
  serviceFeeData?: ServiceFee;
  cartTotalTax = 0;
  selectedPaymentMethod: "pix" | "credit_card" | "cash" = "pix";
  selectedInstallment: InstallmentOption | null = null;
  showAddress = false;
  isSeller = false;
  clientInfo: CartClientInfo = {
    client_name: "",
    client_email: "",
    is_foreign_client: false,
    client_country: "BR",
    client_phone_country_code: "+55",
    client_phone: "",
    client_cpf: "",
    client_document: "",
    client_document_type: "cpf",
  };
  clientInfoHighlightTrigger = 0;

  private destroy$ = new Subject<void>();

  get marketingCheckoutContext(): MarketingJourneyContext | undefined {
    const eventId = this.cartProducts.find(
      (item) => item.status === "active",
    )?.event_id;
    if (this.isSeller || !eventId) return undefined;
    const info = this.eventInfo?.id === eventId ? this.eventInfo : undefined;
    const category = info?.event_category?.title;
    const activeItems = this.cartProducts.filter(
      (item) => item.status === "active" && item.event_id === eventId,
    );
    const items = activeItems.flatMap((item) => {
      const cents =
        item.price == null ? NaN : Math.round(Number(item.price) * 100);
      if (
        !Number.isSafeInteger(item.ticket_id) ||
        item.ticket_id <= 0 ||
        !Number.isSafeInteger(item.quantity) ||
        item.quantity <= 0 ||
        !Number.isSafeInteger(cents) ||
        cents < 0
      )
        return [];
      return [
        {
          ticket_id: item.ticket_id,
          quantity: item.quantity,
          unit_price_cents: cents,
          ...(item.title ? { name: item.title } : {}),
          ...(category ? { category } : {}),
        },
      ];
    });
    return {
      ...(info?.title ? { event_name: info.title } : {}),
      ...(category ? { event_category: category } : {}),
      currency: "BRL",
      ...(items.length === activeItems.length ? { items } : {}),
    };
  }

  get totalItems(): number {
    return this.cartProducts?.reduce((total, item) => total + item.quantity, 0);
  }

  get totalPrice(): number {
    return this.cartProducts?.reduce(
      (total, item) => total + Number(item.price) * item.quantity,
      0,
    );
  }

  get totalTax(): number {
    return this.cartTotalTax;
  }

  get totalConsumption(): number {
    return this.cartProducts?.reduce(
      (total, item) => total + Number(item.consumption ?? 0) * item.quantity,
      0,
    );
  }

  get serviceFee(): number {
    if (!this.serviceFeeData) {
      return 0;
    }
    return this.serviceFeeData[this.selectedPaymentMethod] || 0;
  }

  get totalPriceWithTax(): number {
    return (
      this.totalPrice + this.totalTax + this.totalConsumption + this.serviceFee
    );
  }

  // Desconto do cupom (em reais). Incide só sobre o valor do ingresso.
  get discountAmount(): number {
    return this.appliedCoupon?.discount_amount ?? 0;
  }

  // Total cobrável já com o desconto aplicado (nunca negativo).
  get chargeableTotal(): number {
    return Math.max(0, this.totalPriceWithTax - this.discountAmount);
  }

  // Crédito parcelado (2x+): tem juros (e não a taxa de processamento à vista).
  get hasInstallmentInterest(): boolean {
    return (
      this.selectedPaymentMethod === "credit_card" &&
      !!this.selectedInstallment &&
      !this.selectedInstallment.interest_free
    );
  }

  // Taxa de processamento exibida:
  //   - crédito 2x+: total do parcelado − (ingresso + taxa da plataforma + consumação)
  //     (ou seja, o juros do parcelamento — o custo do PagarMe já está embutido na tabela);
  //   - à vista (PIX / crédito 1x): a taxa de processamento padrão.
  get processingFeeDisplay(): number {
    if (this.hasInstallmentInterest) {
      // O total do parcelado (displayTotal) já vem com o desconto do cupom
      // aplicado na base. Subtraímos o ingresso JÁ com desconto
      // (totalPrice − desconto), senão o desconto abateria a taxa por engano.
      return (
        this.displayTotal -
        (this.totalPrice -
          this.discountAmount +
          this.totalTax +
          this.totalConsumption)
      );
    }
    return this.serviceFee;
  }

  // Total exibido: no crédito 2x+ usa o total da parcela escolhida (com juros);
  // caso contrário, o total à vista.
  get displayTotal(): number {
    if (this.hasInstallmentInterest && this.selectedInstallment) {
      return this.selectedInstallment.total_cents / 100;
    }
    return this.chargeableTotal;
  }

  // Subtotal antes do desconto (soma das linhas acima, inclusive juros).
  // Só faz sentido exibir quando há cupom aplicado.
  get subtotalBeforeDiscount(): number {
    return this.displayTotal + this.discountAmount;
  }

  // Título da categoria do carrinho para um ticket_id (para as mensagens do
  // cupom). Vazio se o ingresso não estiver mais no carrinho.
  couponTicketTitle(ticketId?: number | null): string {
    if (ticketId == null) {
      return "";
    }
    return (
      this.cartProducts.find((item) => item.ticket_id === ticketId)?.title ?? ""
    );
  }

  // "Desconto em 2 de 3 Pista": deixa claro em qual ingresso o cupom caiu e
  // quantas unidades ele cobre neste carrinho.
  get couponCoverageLabel(): string {
    const coupon = this.appliedCoupon;
    if (!coupon?.ticket_id) {
      return "";
    }
    if ((coupon.discount_amount ?? 0) <= 0) {
      return "Cupom de comissário vinculado — sem desconto no valor.";
    }
    const title = this.couponTicketTitle(coupon.ticket_id);
    const item = this.cartProducts.find(
      (product) => product.ticket_id === coupon.ticket_id,
    );
    const quantity = item?.quantity ?? 0;
    const units =
      coupon.discounted_units ??
      Math.min(coupon.max_discounted_items ?? 1, quantity);
    return `Desconto em ${units} de ${quantity} - ${title}`.trim();
  }

  // Só há escolha real de ingresso em cupons antigos multi-categoria
  // (eligible_ticket_ids > 1); com um único ingresso elegível no carrinho é automático.
  get showCouponTicketSelector(): boolean {
    return (this.appliedCoupon?.eligible_ticket_ids?.length ?? 0) > 1;
  }

  get couponEligibleOptions(): { label: string; value: number }[] {
    return (this.appliedCoupon?.eligible_ticket_ids ?? []).map((id) => ({
      label: this.couponTicketTitle(id) || `Ingresso ${id}`,
      value: id,
    }));
  }

  ngOnInit(): void {
    this.verifyAddres();
    this.initializePaymentMethod();
    this.loadCart();
    if (isPlatformBrowser(this.platformId)) this.watchSalesEligibility();
  }

  private watchSalesEligibility(): void {
    // Refresh only eligibility. Do not destroy an already-issued PIX/payment monitor.
    interval(15_000).pipe(
      switchMap(() => this.cartService.getCart().pipe(catchError(() => EMPTY))),
      tap(cart => this.onlineSalesClosed = !!cart.online_sales_closed),
      takeUntil(this.destroy$),
    ).subscribe();
  }

  private initializePaymentMethod(): void {
    const countryCode = (
      this.currentUser?.address?.country ?? "BR"
    ).toUpperCase();
    this.selectedPaymentMethod = countryCode === "BR" ? "pix" : "credit_card";
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.unsubscribe();
  }

  loadEventInfo(eventId: number) {
    this.eventsManagementService.getEventInfo(eventId).subscribe({
      next: (response) => {
        if (response?.id) {
          this.eventInfo = { ...response };
        }
      },
      error: (error) => {
        console.error("Erro ao info do evento:", error);
      },
    });
  }

  addMinutesToDate(date: string, minutes: number): Date {
    // Cria uma nova instância de Date baseada na data passada
    const newDate = new Date(date);
    // Adiciona os minutos à data
    newDate.setMinutes(newDate.getMinutes() + minutes);
    // Retorna a nova data
    return newDate;
  }

  loadCart(): void {
    this.cartService.getCart().subscribe({
      next: (response) => {
        if (response.cart_items) {
          this.onlineSalesClosed = !!response.online_sales_closed;
          // O backend já devolve as taxas efetivamente cobradas do comprador.
          this.serviceFeeData = response.service_fee;
          this.cartTotalTax = Number(response.total_tax ?? 0);
          // Reexibe o cupom aplicado server-side (sobrevive ao reload). Vem
          // `null` quando deixou de valer ou não desconta os itens atuais.
          this.appliedCoupon = response.applied_coupon ?? null;

          this.cartProducts = response.cart_items
            .filter((item) => item.status == "active")
            .sort((a, b) => a.ticket_id - b.ticket_id);

          if (this.cartProducts.length > 0) {
            const eventId = this.cartProducts[0].event_id;
            const dateItemAddedToCard = this.cartProducts[0].created_at;
            const limitDatetime = this.addMinutesToDate(dateItemAddedToCard, 1);
            this.limitDatetime = limitDatetime;

            this.updateSellerStatus(eventId);
            this.loadEventInfo(eventId);
            this.applyPendingCouponIfNeeded(eventId);
          } else {
            this.eventInfo = undefined;
            this.isSeller = false;
          }
        }
      },
      error: (error) => {
        console.error("Erro ao carregar o carrinho:", error);
      },
    });
  }

  onPaymentMethodChange(method: "pix" | "credit_card" | "cash"): void {
    this.selectedPaymentMethod = method;
    if (method !== "credit_card") {
      this.selectedInstallment = null;
    }
  }

  onInstallmentChange(option: InstallmentOption | null): void {
    this.selectedInstallment = option;
  }

  applyCoupon(ticketId?: number): void {
    const code = this.couponControl.value.trim();
    if (!code || this.couponLoading) {
      return;
    }

    this.couponLoading = true;
    this.couponError = "";

    this.couponsService
      .applyToCart(code, ticketId)
      .pipe(
        tap((response) => {
          this.appliedCoupon = this.buildAppliedCoupon(response);
          const eventId = this.cartProducts[0]?.event_id;
          if (eventId) this.pendingCoupon.clear(eventId);
          if ((response.discount_amount ?? 0) <= 0) {
            this.snackbarService.openSnackBar(
              `Cupom ${response.coupon_code} vinculado (sem desconto no valor).`,
              "Fechar",
              "success",
            );
          }
        }),
        catchError((err) => {
          this.appliedCoupon = null;
          this.couponError =
            err?.error?.error ?? "Não foi possível aplicar o cupom.";
          this.snackbarService.openSnackBar(
            this.couponError,
            "Fechar",
            "error",
          );
          return EMPTY;
        }),
        finalize(() => (this.couponLoading = false)),
        takeUntil(this.destroy$),
      )
      .subscribe();
  }

  /** Cupom gravado via /event/:id?cupom=CODE — aplica sozinho no checkout. */
  private applyPendingCouponIfNeeded(eventId: number): void {
    if (this.appliedCoupon || this.couponLoading) return;
    const pending = this.pendingCoupon.getForEvent(eventId);
    if (!pending) return;

    this.couponControl.setValue(pending.code);
    this.applyCoupon();
  }

  // Troca o ingresso onde o cupom incide (cupons antigos multi-categoria). O
  // backend exige remover antes de reaplicar em outro ingresso (§Checkout).
  onCouponTicketChange(ticketId: number): void {
    const code = this.appliedCoupon?.code;
    if (
      !code ||
      this.couponLoading ||
      ticketId === this.appliedCoupon?.ticket_id
    ) {
      return;
    }

    this.couponLoading = true;
    this.couponError = "";

    this.couponsService
      .removeFromCart()
      .pipe(
        switchMap(() => this.couponsService.applyToCart(code, ticketId)),
        tap((response) => {
          this.appliedCoupon = this.buildAppliedCoupon(response);
        }),
        catchError((err) => {
          this.couponError =
            err?.error?.error ?? "Não foi possível trocar o ingresso do cupom.";
          return EMPTY;
        }),
        finalize(() => (this.couponLoading = false)),
        takeUntil(this.destroy$),
      )
      .subscribe();
  }

  private buildAppliedCoupon(response: ApplyCouponResponse): CartAppliedCoupon {
    return {
      code: response.coupon_code,
      discount_amount: response.discount_amount,
      ticket_id: response.ticket_id,
      discounted_units: response.discounted_units,
      max_discounted_items: response.max_discounted_items,
      eligible_ticket_ids: response.eligible_ticket_ids,
    };
  }

  removeCoupon(): void {
    this.couponsService
      .removeFromCart()
      .pipe(
        // Mesmo se a remoção falhar no servidor, limpa localmente para não
        // travar o checkout com um desconto que não vale mais.
        tap(() => this.clearCoupon()),
        catchError(() => {
          this.clearCoupon();
          return EMPTY;
        }),
        takeUntil(this.destroy$),
      )
      .subscribe();
  }

  // Pagamento bloqueado por cupom esgotado/expirado (§8): limpa o cupom (total
  // volta sem desconto) e avisa o usuário. A remoção no servidor é best-effort.
  handleCouponExpired(): void {
    this.couponsService
      .removeFromCart()
      .pipe(
        catchError(() => EMPTY),
        takeUntil(this.destroy$),
      )
      .subscribe();

    this.appliedCoupon = null;
    this.couponControl.reset("");
    this.couponError =
      "O cupom expirou ou esgotou. O total foi recalculado sem o desconto.";
  }

  private clearCoupon(): void {
    this.appliedCoupon = null;
    this.couponControl.reset("");
    this.couponError = "";
  }

  formataValorEmReais(valor: number | string): string {
    return this.utilsService.formataValorEmReais(valor);
  }

  openModalSignUp() {
    const dialogRef = this.dialog.open(ModalSignupComponent, {
      width: "800px",
      maxWidth: "95vw",
    });

    dialogRef
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.currentUser = this.authService.getLocalStorageUserInfo();
      });
  }

  handleContinueToCheckoutClick() {
    this.showAddress = false;
  }
  handleBackToaddressClick() {
    if (this.isSeller) {
      this.showAddress = false;
      return;
    }
    this.showAddress = true;
  }

  handleClientInfoChange(clientInfo: CartClientInfo) {
    this.clientInfo = clientInfo;
  }

  handleRequestClientInfoHighlight() {
    this.clientInfoHighlightTrigger++;
  }

  private verifyAddres() {
    this.currentUser = this.authService.getLocalStorageUserInfo();

    this.showAddress =
      !this.currentUser?.address || !this.currentUser?.address.postal_code;
  }

  updateSellerStatus(eventId: number): void {
    const assignments = this.currentUser?.seller_assignments ?? [];

    this.isSeller = assignments.some((assignment) => {
      if (typeof assignment === "number") {
        return assignment === eventId;
      }

      return (
        assignment.resource_type === "Event" &&
        assignment.resource_id === eventId
      );
    });

    if (this.isSeller) {
      this.showAddress = false;
    }
  }
}
